import { useMemo } from 'react'

import { useLinkAccess, useRegistry } from './registry-context'
import type { AdminHomeCategory, AdminHomeLink, AdminHomeSection, LinkAccess } from './types'

/** Whether an entry a `LinkAccess` is asked about is an Admin Home link. */
export function isAdminHomeLink(entry: Parameters<LinkAccess>[0]): entry is AdminHomeLink {
  return 'category' in entry
}

/** Where Admin Home lives, and the query parameter that opens one of its sections. */
export const adminHomePath = '/admin'
export const adminHomeSectionParam = 'section'

/** The address of Admin Home, showing one section when `sectionId` is given. */
export function adminHomeHref(sectionId?: string): string {
  return sectionId === undefined
    ? adminHomePath
    : `${adminHomePath}?${new URLSearchParams({ [adminHomeSectionParam]: sectionId }).toString()}`
}

export interface AdminHomeGroup {
  readonly section: AdminHomeSection
  readonly categories: readonly {
    readonly category: AdminHomeCategory
    readonly links: readonly AdminHomeLink[]
  }[]
}

/**
 * Admin Home's sections in registry order, each with the categories that hold a link the viewer
 * may see; empty categories and sections are left out.
 */
export function useAdminHomeGroups(): readonly AdminHomeGroup[] {
  const { adminHome } = useRegistry()
  const canSee = useLinkAccess()

  return useMemo(
    () =>
      adminHome.sections
        .map((section) => ({
          section,
          categories: adminHome.categories
            .filter((category) => category.section === section.id)
            .map((category) => ({
              category,
              links: adminHome.links.filter(
                (link) => link.category === category.id && canSee(link),
              ),
            }))
            .filter((entry) => entry.links.length > 0),
        }))
        .filter((group) => group.categories.length > 0),
    [adminHome, canSee],
  )
}
